import { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Eye, EyeOff, Mail, Glasses } from 'lucide-react';
import { useAuth } from '../AuthModule';
import {
  API,
  DEFAULT_BUBBLE_COLOR_HEX,
  PROFILE_FIELDS,
  defaultProfile,
  profileToDisplay,
  toColorInputValue,
  normalizeHexTo6,
  getLighterBorderColor,
  getContrastingTextColor,
  AvatarFallback,
} from './profileHelpers';
import AvatarWithDesignerBadge from './AvatarWithDesignerBadge';
import ProfileNavTopBar from '../ProfileNavTopBar';
import { useT } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';

const PROFILE_TITLE_USERNAME_MAX = 20;

function truncateTitleUsername(value) {
  const raw = String(value || '').trim();
  if (raw.length <= PROFILE_TITLE_USERNAME_MAX) return raw;
  return `${raw.slice(0, PROFILE_TITLE_USERNAME_MAX)}...`;
}

function RatingCommentRow({ authorName, authorBubbleColor, reason }) {
  const displayName = authorName || '—';
  const hex = normalizeHexTo6(authorBubbleColor) || DEFAULT_BUBBLE_COLOR_HEX;
  const bg = toColorInputValue(hex);
  const textColor = getContrastingTextColor(hex);
  return (
    <>
      <hr />
      <div className="Profile-rating-comment-inner">
        <span
          className="Profile-rating-comment-author-bubble"
          style={{ backgroundColor: bg, color: textColor }}
        >
          {displayName}
        </span>
        <span className="Profile-rating-comment-text">{reason || '(ei kommenttia)'}</span>
      </div>
    </>
  );
}
const NOTIFICATION_TTL_MS = 4000;

export default function UserProfilePage({ user }) {
  const { logout } = useAuth();
  const t = useT();
  const [profile, setProfile] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotificationState] = useState(null);
  const setNotification = (message, variant) =>
    setNotificationState(message == null ? null : { message, variant });
  const [authoredEventsData, setAuthoredEventsData] = useState(null);
  const [hostedRatingsData, setHostedRatingsData] = useState(null);
  const [avatarImageError, setAvatarImageError] = useState(false);

  const userId = user?.id;
// Notification fade out
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotificationState(null), NOTIFICATION_TTL_MS);
    return () => clearTimeout(timer);
  }, [notification]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`).then((res) => res.json()),
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`).then((res) => res.json()),
    ])
      .then(([profileData, settingsData]) => {
        const p = profileData && typeof profileData === 'object' ? profileData : null;
        setProfile(p);
        setUserSettings(settingsData && typeof settingsData === 'object' ? settingsData : null);
        setAvatarImageError(false);
      })
      .catch(() => {
        setProfile(null);
        setUserSettings(null);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (!userId || !profile) return;
    const base = { ...defaultProfile(), ...profile };
    if (base.showEventsHosted !== 'YES' && base.showEventRatings !== 'YES') {
      setAuthoredEventsData({ count: 0, events: [] });
      return;
    }
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/authored-events`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.count === 'number' && Array.isArray(data.events)) {
          setAuthoredEventsData({ count: data.count, events: data.events });
        } else {
          setAuthoredEventsData({ count: 0, events: [] });
        }
      })
      .catch(() => setAuthoredEventsData({ count: 0, events: [] }));
  }, [userId, profile]);

  useEffect(() => {
    if (!userId || !profile) return;
    const base = { ...defaultProfile(), ...profile };
    if (base.showEventRatings !== 'YES') {
      setHostedRatingsData(null);
      return;
    }
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/hosted-event-ratings`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.totalRatings === 'number' && Array.isArray(data.ratings)) {
          setHostedRatingsData({
            totalRatings: data.totalRatings,
            averageRating: data.averageRating ?? 0,
            ratings: data.ratings,
          });
        } else {
          setHostedRatingsData({ totalRatings: 0, averageRating: 0, ratings: [] });
        }
      })
      .catch(() => setHostedRatingsData({ totalRatings: 0, averageRating: 0, ratings: [] }));
  }, [userId, profile]);

  const toggleVisibility = (visibilityKey) => {
    if (!userId) return;
    const current = displayProfile[visibilityKey] === 'YES';
    const next = current ? 'NO' : 'YES';
    const payload = {
      showAvatar: displayProfile.avatar,
      showAllowDms: displayProfile.showAllowDms,
      showContactEmail: displayProfile.showContactEmail || 'NO',
      showWebsite: displayProfile.showWebsite || 'NO',
      showEventsHosted: displayProfile.showEventsHosted || 'YES',
      showEventRatings: displayProfile.showEventRatings || 'YES',
    };
    if (visibilityKey === 'avatar') {
      payload.showAvatar = next;
    } else if (visibilityKey === 'showDescription') {
      // Keep both camelCase and snake_case in sync with the backend/profile, and mirror into userSettings
      payload.showDescription = next;
      payload.show_description = next;
    } else {
      payload[visibilityKey] = next;
    }
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((profileData) => {
        setProfile(profileData);
        setNotification(t('profile.visibilityUpdated'), 'success');
      })
      .catch(() => setNotification('Näkyvyyden päivitys epäonnistui.', 'error'));
  };

  const displayProfile = profileToDisplay(profile);

  const renderStaticValue = (field, value) => {
    if (field.type === 'yesno' || field.type === 'image') return value === 'YES' ? 'Kyllä' : 'Ei';
    return value ?? '—';
  };

  const handleLogout = () => {
    logout();
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  const descriptionHeader =
    userSettings?.descriptionHeader && String(userSettings.descriptionHeader).trim();
  const descriptionBody =
    userSettings?.descriptionBody && String(userSettings.descriptionBody).trim();
  const hasDescription = !!(descriptionHeader || descriptionBody);
  const showDescriptionFlag = displayProfile.showDescription;

  return (
    <div className="App-block App-block--profile-page-shell">
      <div className="App-block--profile-inner">
        <ProfileNavTopBar user={user} currentPage="profile" />
        {loading ? (
          <p>{loadingPageText(t, 'profile')}</p>
        ) : (
          <>
            <div className="Profile-view">
            <div className="Profile-header Profile-header--full-width">
              <h2 className="Profile-heading">
                {truncateTitleUsername(user.displayName || user.username)} :: {t('nav.profile')}
              </h2>
              {user?.displayName && (
                <a
                  href={`/profiili/${encodeURIComponent(user.displayName)}`}
                  className="Profile-public-link"
                  title="Näytä julkinen profiili"
                >
                  <Glasses size={18} />
                </a>
              )}
            </div>
            {notification && (
              <span
                className={`Profile-notification${
                  notification.variant ? ` Profile-notification--${notification.variant}` : ''
                }`}
                role="status"
                aria-live="polite"
              >
                {notification.message}
              </span>
            )}
            <div className="Profile-avatar-wrap">
              <div className="Profile-field-row">
                <span className="Profile-field-value">
                  <AvatarWithDesignerBadge designer={userSettings?.avatarDesigner} variant="public">
                    {displayProfile.avatar !== 'YES' || !(profile?.avatar) || avatarImageError ? (
                      <AvatarFallback
                        bubbleColorHex={userSettings?.bubbleColor}
                        displayName={user?.displayName}
                        username={user?.username}
                        size="large"
                      />
                    ) : (
                      <img
                        src={profile.avatar}
                        alt=""
                        className="Profile-avatar Profile-avatar-inline"
                        onError={() => setAvatarImageError(true)}
                      />
                    )}
                  </AvatarWithDesignerBadge>
                </span>
                <button
                  type="button"
                  className="Profile-visibility-toggle"
                  onClick={() => toggleVisibility('avatar')}
                  title={displayProfile.avatar === 'YES' ? 'Näytä muille' : 'Piilota muilta'}
                  aria-label={displayProfile.avatar === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
                >
                  {displayProfile.avatar === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              <div
                className="Profile-bubble-preview"
                style={{
                  backgroundColor: toColorInputValue(normalizeHexTo6(userSettings?.bubbleColor) || DEFAULT_BUBBLE_COLOR_HEX),
                  border: `1px solid ${getLighterBorderColor(userSettings?.bubbleColor)}`,
                }}
                aria-hidden
              >
                MOI! Minä olen {user?.displayName || user?.username}
              </div>
              <div className="Profile-field-row">
                <div className="Profile-message-button-wrap">
                  <span
                    className={`Profile-message-button${
                      displayProfile.showAllowDms !== 'YES' ? ' Profile-message-button--dimmed' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                  >
                    <Mail className="Profile-message-button-icon" /> {t('profile.sendDm')}
                  </span>
                </div>

                <button
                  type="button"
                  className="Profile-visibility-toggle"
                  onClick={() => toggleVisibility('showAllowDms')}
                  title={displayProfile.showAllowDms === 'YES' ? 'Näytä muille' : 'Piilota muilta'}
                  aria-label={displayProfile.showAllowDms === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
                >
                  {displayProfile.showAllowDms === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>  
            </div>

            <div className="user-settings">
              {PROFILE_FIELDS.filter((f) => f.key !== 'avatar' && f.key !== 'allowDms').map((field) => {
                const IconComponent =
                  field.type === 'icon' && field.label && LucideIcons[field.label.charAt(0).toUpperCase() + field.label.slice(1)];
                const isHidden = displayProfile[field.visibilityKey] !== 'YES';
                return (
                  <div
                    key={field.key}
                    className={`Profile-field Profile-field-row${isHidden ? ' Profile-field--hidden' : ''}`}
                  >
                    <span className="Profile-field-label" aria-label={field.type === 'icon' ? field.key : undefined}>
                      {field.type === 'icon' && IconComponent ? <IconComponent size={18} /> : field.label}
                    </span>
                    <span className="Profile-field-value">
                      {renderStaticValue(field, displayProfile[field.key])}
                    </span>
                    <button
                      type="button"
                      className="Profile-visibility-toggle"
                      onClick={() => toggleVisibility(field.visibilityKey)}
                      title={displayProfile[field.visibilityKey] === 'YES' ? 'Näytä muille' : 'Piilota muilta'}
                      aria-label={displayProfile[field.visibilityKey] === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
                    >
                      {displayProfile[field.visibilityKey] === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                  </div>
                );
              })}
            </div>

            {hasDescription && (
              <>
                <div className="Profile-field-row Profile-section-row">
                  <h3
                    className={`Profile-section-title${
                      showDescriptionFlag !== 'YES' ? ' Profile-section-title--hidden' : ''
                    }`}
                  >
                    {descriptionHeader || 'Kuvaus'}
                  </h3>
                  <button
                    type="button"
                    className="Profile-visibility-toggle"
                    onClick={() => toggleVisibility('showDescription')}
                    title={showDescriptionFlag === 'YES' ? 'Piilota muilta' : 'Näytä muille'}
                    aria-label={showDescriptionFlag === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
                  >
                    {showDescriptionFlag === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
                {showDescriptionFlag === 'YES' && descriptionBody && (
                  <p className="Profile-section-note">{descriptionBody}</p>
                )}
              </>
            )}

            <div className="Profile-field-row Profile-section-row">
              <h3 className={`Profile-section-title${displayProfile.showEventsHosted !== 'YES' ? ' Profile-section-title--hidden' : ''}`}>
                {t('profile.organizedEvents')}
              </h3>
              <button
                type="button"
                className="Profile-visibility-toggle"
                onClick={() => toggleVisibility('showEventsHosted')}
                title={displayProfile.showEventsHosted === 'YES' ? 'Näytä muille' : 'Piilota muilta'}
                aria-label={displayProfile.showEventsHosted === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
              >
                {displayProfile.showEventsHosted === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {displayProfile.showEventsHosted === 'YES' && authoredEventsData && (
              authoredEventsData.count === 0 ? (
                <p className="Profile-section-note">{t('profile.noOrganizedEvents')}</p>
              ) : authoredEventsData.count > 0 ? (
                <p className="Profile-section-note">({authoredEventsData.count} {t('profile.eventsUnit')})</p>
              ) : null
            )}
            <div className="Profile-field-row Profile-section-row">
              <h3
                id="profile-arvostelut-heading"
                className={`Profile-section-title${displayProfile.showEventRatings !== 'YES' ? ' Profile-section-title--hidden' : ''}`}
              >
                {t('profile.reviews')}
              </h3>
              <button
                type="button"
                className="Profile-visibility-toggle"
                onClick={() => toggleVisibility('showEventRatings')}
                title={displayProfile.showEventRatings === 'YES' ? 'Näytä muille' : 'Piilota muilta'}
                aria-label={displayProfile.showEventRatings === 'YES' ? 'Näkyy muille' : 'Piilotettu'}
              >
                {displayProfile.showEventRatings === 'YES' ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {displayProfile.showEventRatings === 'YES' && (
              <section className="Profile-ratings-section" aria-labelledby="profile-arvostelut-heading">
                {hostedRatingsData == null ? (
                  <p className="Profile-ratings-empty">Ladataan…</p>
                ) : hostedRatingsData.totalRatings === 0 ? (
                  <p className="Profile-ratings-empty">{t('profile.noReviews')}</p>
                ) : (
                  <>
                    <p className="Profile-ratings-summary">
                      Arvosteluja {hostedRatingsData.totalRatings}. KA: {hostedRatingsData.averageRating} / 5
                    </p>
                    <ul className="Profile-ratings-comments-list">
                      {hostedRatingsData.ratings.map((r, i) => (
                        <li key={i} className="Profile-rating-comment">
                          <RatingCommentRow
                            authorName={r.authorName}
                            authorBubbleColor={r.authorBubbleColor}
                            reason={r.reason}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}
          </div>

            <div className="Settings-logout-section">
              <button
                type="button"
                className="Button Settings-logout-button"
                onClick={handleLogout}
              >
                {t('settings.logout')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
